import {
  getDataImportStatus,
  getDataRecalculationStatus,
} from '../../../api/dataInputApi';
import {
  ACTIVE_RECALCULATION_STATUSES,
  TERMINAL_RECALCULATION_STATUSES,
  normalizeImportOperation,
  normalizeRecalculationOperation,
} from '../model/dataInputModel';
import {
  isActiveImportStatus,
  isTerminalImportStatus,
} from '../model/dataInputActions';
import pollKnownOperationStatus from '../../../shared/utils/pollKnownOperationStatus';

const isActiveRecalculationStatus = status => (
  ACTIVE_RECALCULATION_STATUSES.has(String(status || '').toUpperCase())
);
const isTerminalRecalculationStatus = status => (
  TERMINAL_RECALCULATION_STATUSES.has(String(status || '').toUpperCase())
);

export async function waitForImport(
  importId,
  {
    signal, initialOperation, onProgress, pollIntervalMs,
  },
) {
  const statusUrl = initialOperation?.statusUrl;
  return pollKnownOperationStatus({
    operationId: importId,
    initialOperation,
    fetchStatus: async ({ signal: requestSignal }) => {
      const operation = normalizeImportOperation(
        await getDataImportStatus(statusUrl, importId, { signal: requestSignal }),
      );
      if (!operation) throw new Error('Invalid import status response.');
      return operation;
    },
    isActiveStatus: isActiveImportStatus,
    isTerminalStatus: isTerminalImportStatus,
    onStatus: onProgress,
    signal,
    intervalMs: pollIntervalMs,
    monitoringErrorCode: 'IMPORT_STATUS_UNAVAILABLE',
  });
}

export async function waitForRecalculation(
  fiscalYear,
  recalculationId,
  {
    signal, initialOperation, onStatus, pollIntervalMs,
  },
) {
  const statusUrl = initialOperation?.statusUrl;
  return pollKnownOperationStatus({
    operationId: recalculationId,
    initialOperation,
    fetchStatus: async ({ signal: requestSignal }) => {
      const operation = normalizeRecalculationOperation(
        await getDataRecalculationStatus(
          statusUrl,
          fiscalYear,
          recalculationId,
          { signal: requestSignal },
        ),
      );
      if (!operation) throw new Error('Invalid recalculation status response.');
      return operation;
    },
    isActiveStatus: isActiveRecalculationStatus,
    isTerminalStatus: isTerminalRecalculationStatus,
    onStatus,
    signal,
    intervalMs: pollIntervalMs,
    monitoringErrorCode: 'RECALCULATION_STATUS_UNAVAILABLE',
  });
}

export function importOperationError(operation) {
  const error = new Error(operation.message || 'IMPORT_PROCESSING_FAILED');
  error.operation = operation;
  error.partialDataPossible = Boolean(operation.partialDataPossible);
  error.recoveryAction = operation.recoveryAction || null;
  return error;
}

export function recalculationOperationError(operation) {
  const error = new Error(operation.message || 'CALCULATION_PERSISTENCE_FAILED');
  error.operation = operation;
  return error;
}
